// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import isTouchDevice from 'is-touch-device';
import { analytics, analyticsActions, Track } from 'angieslist-app-platform';
import { Alert, Button, Modal, Grid, Row, Col, Collapse } from 'angieslist-react-components';
import { stylesColors } from 'angieslist-styles';
import { reduxForm, Form } from 'redux-form';
import { compose, pure } from 'recompose';
// $FlowFixMe
import { is, Set } from 'immutable';
import { difference, get, uniq } from 'lodash';
import classNames from 'classnames';
// $FlowFixMe
import 'mapbox-gl/dist/mapbox-gl.css';
import urlCatalog from '@angi/url-catalog';
import CitySelection from './CitySelection';
import serviceAreaMapActions from '../../common/service-area/serviceAreaMapActions';
import type { ServiceAreaMapReducerStateInterface } from '../../common/service-area/serviceAreaMapReducerTypes';
import { defaultProps } from '../analyticsDefaultProperties';
import type { LatLong, ServiceProviderCacheState, ServiceProviderIdState } from '../../types';
import serviceAreaMapAnalytics from './serviceAreaMapAnalytics';
import './serviceAreaMap.scss';
import ButtonWithTooltip from './ButtonWithTooltip';

const mapboxgl = typeof window !== 'undefined' ? require('mapbox-gl') : {};

type Props = {
  advertisingArea: string[],
  serviceArea: string[],
  serviceProviderLocation: LatLong,
  showEditControls: boolean,
  handleSubmit: any,
  // redux
  config: any,
  serviceAreaMapStore: ServiceAreaMapReducerStateInterface,
  serviceProviderCache: ServiceProviderCacheState,
  serviceProviderId: string,
  serviceProviderIdFromState: ServiceProviderIdState,
  setServiceArea: typeof serviceAreaMapActions.setServiceArea,
  resetSetServiceArea: typeof serviceAreaMapActions.resetSetServiceArea,
  sendEnhancedTrackEvent: typeof analyticsActions.sendEnhancedTrackEvent,
};
type State = {
  legendOpened: boolean,
  citySelectionOpened: boolean,
  editMode: boolean,
  confirmEditServiceAreaModal: boolean,
  confirmResetServiceAreaModal: boolean,
  showEditAlert: boolean,
  showSavedAlert: boolean,
  showClickedAdAreaAlert: boolean,
  showZoomInAlert: boolean,
  draftServiceArea: Set<string>
}

const usingTouchDevice = isTouchDevice();

const legendData = [
  {
    label: 'Free areas',
    color: stylesColors.brandPrimary,
    opacity: 0.5,
  },
  {
    label: 'Paid areas',
    color: stylesColors.brandInfo,
    opacity: 0.5,
  },
];

const dismissAlertAfter = 2500;

const layers = Object.freeze({
  postalCodeBoundaries: 'postal-boundaries',
  postalCodes: 'postal-codes',
  serviceArea: 'service-area',
  advertisingArea: 'advertising-area',
});
const beforeUnload = 'beforeunload';

// This represents the minimum map level necessary to both show zip boundaries and allow editing
const minEditZoomLevel = 5.01;

const mapLegendItem = legendItem => (
  <Col xs={6} sm={4} key={legendItem.label} className="legend-column">
    <i
      className="icon icon-square"
      style={{
      color: legendItem.color,
      opacity: legendItem.opacity,
      marginBottom: 2,
    }}
    />
    <span style={{ marginLeft: 3 }}>{legendItem.label}</span>
  </Col>);

const generateLegend = () => legendData.map(mapLegendItem);


const renderAlertBodyWithIcon = (text: string, iconClass: string) => (
  <div className="map-alert-body">
    <div className="map-alert-icon-container">
      <i className={`icon icon-2x ${iconClass}`} />
    </div>
    <span>
      {text}
    </span>
  </div>
);

const getMapboxPostalCode = (postalCode: string) => `USP4${postalCode}`;

export class ServiceAreaMapComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      legendOpened: false,
      citySelectionOpened: false,
      editMode: !props.showEditControls, // if no controls, default to edit mode
      confirmEditServiceAreaModal: false,
      confirmResetServiceAreaModal: false,
      showEditAlert: false,
      showSavedAlert: false,
      showZoomInAlert: false,
      showClickedAdAreaAlert: false,
      // Fork Redux service area and use draftServiceArea everywhere internally.
      // Reset to Redux version only on cancel edit.
      draftServiceArea: Set(props.serviceArea),
    };
  }

  state: State;

  componentDidMount() {
    window.addEventListener('resize', this.resize);

    mapboxgl.accessToken = this.props.config.mapbox_api_key;

    this.map = new mapboxgl.Map({
      attributionControl: false,
      container: this.mapContainer,
      style: 'mapbox://styles/mapbox/light-v9',
      zoom: 9,
    });
    this.map.jumpTo({
      center: [this.props.serviceProviderLocation.lon, this.props.serviceProviderLocation.lat],
    });
    this.map.on('load', () => {
      this.map.getCanvas().style.cursor = 'pointer';
      this.map.addSource('postal-4', {
        type: 'vector',
        // use "full detail postal code areas" tileset
        url: 'mapbox://mapbox.enterprise-boundaries-p4-v2',
      });

      // postal code boundary lines
      this.map.addLayer({
        id: layers.postalCodeBoundaries,
        type: 'line',
        source: 'postal-4',
        'source-layer': 'boundaries_postal_4',
        layout: {},
        paint: {
          'line-color': stylesColors.brandPrimary,
          'line-width': 1,
        },
        filter: [
          'all',
          ['>', 'id', 'USP400000'],
          ['<', 'id', 'USP499999'],
        ],
        minzoom: minEditZoomLevel,
      });

      // postal code fill
      this.map.addLayer({
        id: layers.postalCodes,
        type: 'fill',
        source: 'postal-4',
        'source-layer': 'boundaries_postal_4',
        paint: {
          // invisible layer for handling clicks
          'fill-opacity': 0,
        },
        filter: [
          'all',
          ['>', 'id', 'USP400000'],
          ['<', 'id', 'USP499999'],
        ],
      });

      this.addServiceAreaLayers();
      this.map.dragRotate.disable();
      this.map.touchZoomRotate.disableRotation();

      this.map.on('click', layers.postalCodes, (e) => {
        const zoomLevel = this.map.getZoom();
        if (this.state.editMode) {
          if (zoomLevel < minEditZoomLevel) {
            this.setState(() => ({ showZoomInAlert: true }));
          }
          if (e.features.length > 0 && zoomLevel > minEditZoomLevel) {
            const clickedPostalCode = e.features[0].properties.id.slice(-5);
            if (this.props.advertisingArea.includes(clickedPostalCode)) {
              // show clicked ad area alert if it's not already shown
              this.setState((prevState) => {
                if (prevState.showClickedAdAreaAlert) {
                  return prevState;
                }
                return {
                  showClickedAdAreaAlert: true,
                };
              });
              this.sendAdZoneSelectEvent('Service Area - Map Click', {
                zoomLevel,
              });
            } else {
              this.togglePostalCodeInDraftServiceArea(clickedPostalCode, 'Service Area - Map Click', {
                zoomLevel,
              });
            }
          }
        } else {
          this.setState(() => ({ confirmEditServiceAreaModal: true }));
        }
      });


      const postalCodePopup = new mapboxgl.Popup({
        closeButton: false,
      });

      this.map.on('mousemove', (e) => {
        if (usingTouchDevice) return;

        const features = this.map.queryRenderedFeatures(e.point, { layers: [layers.postalCodes] });

        if (this.map.getZoom() > 8) {
          if (!features.length) {
            postalCodePopup.remove();
            return;
          }
          const feature = features[0];
          postalCodePopup.setLngLat(e.lngLat)
            .setHTML(`<p>${feature.properties.id.slice(4)}</p>`)
            .addTo(this.map);
        } else {
          postalCodePopup.remove();
        }
      });


      // Set the map's default location marker
      this.marker = new mapboxgl.Marker({
        color: '#000000',
      })
        .setLngLat([
          this.props.serviceProviderLocation.lon,
          this.props.serviceProviderLocation.lat,
        ])
        .addTo(this.map);
    });
    this.map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'top-right');
    window.addEventListener(beforeUnload, this.handleOnBeforeUnload);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { lon, lat } = this.props.serviceProviderLocation;
    if (
      lon !== prevProps.serviceProviderLocation.lon
      ||
      lat !== prevProps.serviceProviderLocation.lat
    ) {
      this.map.jumpTo({
        center: [lon, lat],
      });
      if (this.marker) {
        this.marker.setLngLat([lon, lat]);
      }
    }

    // keep service area filter in sync with draft service area
    if (!is(this.state.draftServiceArea, prevState.draftServiceArea)) {
      this.updateServiceAreaFilter();
    }
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove();
    }
    window.removeEventListener(beforeUnload, this.handleOnBeforeUnload);
    window.removeEventListener('resize', this.resize);
  }

  getMapFilters: (() => {|
    advertisingAreaFilter: boolean | Array<any | string | boolean | Array<string>>,
    serviceAreaFilter: boolean | Array<any | string | boolean | Array<string>>,
  |}) = () => {
    const { advertisingArea } = this.props;
    const { draftServiceArea } = this.state;
    // mapbox stores postal codes as "USP4XXXXX", so transform our data to match
    // Also, make sure that all postal codes are unique otherwise mapbox throws
    // an error when setting the filter
    const transformedAdPostalCodes = uniq(advertisingArea).map(getMapboxPostalCode);
    const transformedPostalCodes = draftServiceArea.toArray().map(getMapboxPostalCode);
    // const transformedPostalCodes = ['46123'].map(getMapboxPostalCode);
    const nonAdvertisingServiceArea = difference(
      transformedPostalCodes,
      transformedAdPostalCodes,
    );

      // mapbox throws an error if we attempt to apply a filter against an empty set,
      // so omit the filter if arrays are empty
    return {
      serviceAreaFilter: nonAdvertisingServiceArea.length === 0 ? false : ['match', ['get', 'id'], nonAdvertisingServiceArea, true, false],
      advertisingAreaFilter: transformedAdPostalCodes.length === 0 ? false : ['match', ['get', 'id'], transformedAdPostalCodes, true, false],
    };
  }

  resize: (() => void) = () => this.forceUpdate();

  props: Props;
  map: any;
  mapContainer: any;
  marker: ?{
    setLngLat: (number[]) => void
  };

  handleOnBeforeUnload: ((event: any) => void) = (event: any) => {
    if (this.state.editMode) {
      // Cancel the event as stated by the standard.
      event.preventDefault();
      // Chrome requires returnValue to be set.
      // eslint-disable-next-line
      event.returnValue = '';
    }
  }

  toggleLegend: (() => void) = () => {
    this.setState(prevState => ({ legendOpened: !prevState.legendOpened }));
  }

  sendAdZoneSelectEvent: ((analyticsEventName: string, analyticsProps: Object) => void) = (
    analyticsEventName: string,
    analyticsProps: {},
  ) => {
    this.props.sendEnhancedTrackEvent({
      name: analyticsEventName,
      properties: {
        ...defaultProps,
        ...analyticsProps,
        description: 'Action: AdZone',
      },
    });
  }

  togglePostalCodeInDraftServiceArea: ((
  postalCode: string,
  analyticsEventName: string,
  analyticsProps: {}
) => void) = (
  postalCode: string,
  analyticsEventName: string,
  analyticsProps: {},
) => {
  this.setState(({ draftServiceArea }, { sendEnhancedTrackEvent }) => {
    let description;
    let newDraftServiceArea;
    if (draftServiceArea.has(postalCode)) {
      description = 'Action: Deselect';
      newDraftServiceArea = draftServiceArea.delete(postalCode);
    } else {
      description = 'Action: Select';
      newDraftServiceArea = draftServiceArea.add(postalCode);
    }
    sendEnhancedTrackEvent({
      name: analyticsEventName,
      properties: {
        ...defaultProps,
        ...analyticsProps,
        description,
      },
    });
    return {
      draftServiceArea: newDraftServiceArea,
    };
  });
}

  resetDraftServiceArea: (() => void) = () => {
    this.setState((prevState, props) => ({
      draftServiceArea: Set(props.serviceArea),
    }));
  }

  saveServiceArea: (() => Promise<void> | Promise<mixed>) = async () => {
    const {
      serviceProviderIdFromState,
      setServiceArea,
      serviceProviderId,
    } = this.props;

    const currentServiceProviderId = serviceProviderIdFromState || serviceProviderId;
    const postalCodes = this.state.draftServiceArea.toArray();
    try {
      await setServiceArea(String(currentServiceProviderId), postalCodes);
      this.toggleEditMode();
      this.setState(
        { showSavedAlert: true, citySelectionOpened: false },
        () => this.map.resize(),
      );
      setTimeout(() => {
        this.setState({ showSavedAlert: false });
      }, dismissAlertAfter);
      // $FlowFixMe
      return Promise.resolve();
    } catch (e) {
      // $FlowFixMe
      return Promise.reject(e);
      // ignore here, AyncRequest error will be rendered eventually
    }
  }

  toggleCitySelection: (() => void) = () => {
    this.props.sendEnhancedTrackEvent({
      name: 'Item Clicked',
      properties: {
        ...defaultProps,
        description: this.state.citySelectionOpened ? 'Hide Zip Code list' : 'Show Zip Code list',
      },
    });
    this.setState(
      prevState => ({ citySelectionOpened: !prevState.citySelectionOpened }),
      () => this.map.resize(),
    );
  }

  cancelEditMode: (() => void) = () => {
    this.toggleEditMode();
    if (this.state.citySelectionOpened) {
      this.props.sendEnhancedTrackEvent({
        name: 'Item Clicked',
        properties: {
          ...defaultProps,
          description: 'Hide Zip Code list',
        },
      });
      this.setState(
        { citySelectionOpened: false },
        () => this.map.resize(),
      );
    }
    this.resetDraftServiceArea();
  }

  toggleEditMode: (() => void) = () => {
    this.setState(prevState => ({
      editMode: !prevState.editMode,
      showEditAlert: !prevState.editMode,
    }));
  }

  updateServiceAreaFilter: (() => void) = () => {
    const { serviceAreaFilter } = this.getMapFilters();
    this.map.setFilter(layers.serviceArea, serviceAreaFilter);
  }

  addServiceAreaLayers: (() => void) = () => {
    const { advertisingAreaFilter, serviceAreaFilter } = this.getMapFilters();

    // add layer for service area
    this.map.addLayer({
      id: layers.serviceArea,
      type: 'fill',
      source: 'postal-4',
      'source-layer': 'boundaries_postal_4',
      paint: {
        'fill-color': stylesColors.brandPrimary,
        'fill-opacity': 0.5,
      },
      filter: serviceAreaFilter,
    });

    // add layer for advertising area
    if (advertisingAreaFilter) {
      this.map.addLayer({
        id: layers.advertisingArea,
        type: 'fill',
        source: 'postal-4',
        'source-layer': 'boundaries_postal_4',
        paint: {
          'fill-color': stylesColors.brandInfo,
          'fill-opacity': 0.5,
        },
        filter: advertisingAreaFilter,
      });
    }
  };

  zoomIn: (() => void) = () => { this.map.zoomIn(); }

  zoomOut: (() => void) = () => { this.map.zoomOut(); }

  returnToDefaultLocation: (() => void) = () => {
    this.map.flyTo({
      center: [this.props.serviceProviderLocation.lon, this.props.serviceProviderLocation.lat],
      zoom: 9,
    });
  }

  editServiceArea: (() => void) = () => {
    this.setState({
      // close modal
      confirmEditServiceAreaModal: false,
    }, () => {
      // trigger function in parent to enter edit mode
      this.toggleEditMode();
    });
  }

  openResetServiceAreaModal: (() => void) = ():void => {
    this.setState(() => ({ confirmResetServiceAreaModal: true }));
  }

  resetServiceArea: (() => Promise<void>) = async () => {
    const postalCodes = this.props.advertisingArea;
    this.setState((prevState, props) => ({
      draftServiceArea: Set(postalCodes),
      confirmResetServiceAreaModal: false,
    }));
  }

  render(): React.Node {
    const legendIcon = this.state.legendOpened ? 'icon-down' : 'icon-up';
    const {
      showEditAlert, showSavedAlert, showClickedAdAreaAlert, showZoomInAlert,
    } = this.state;
    const {
      resetSetServiceArea,
      serviceAreaMapStore: { setServiceArea: { error, isLoading } },
      handleSubmit,
      showEditControls,
      serviceProviderIdFromState,
      serviceProviderCache,
    } = this.props;

    const editServiceAreaBtnClassNames = (isNorthStar: boolean, isAdvertiser: boolean) => {
      let classes = 'edit-service-area-btn';
      if (isNorthStar) {
        classes += ' service-area-map-btn-ns';
      }
      if (!isAdvertiser) {
        classes += ' pull-right';
      } else {
        classes += ' service-area-margin-32';
      }
      return classes;
    };

    const intercomAppId = get(this.props.config, 'intercomAppId');
    const { isAdvertiser, isNorthStar } = get(serviceProviderCache, serviceProviderIdFromState, {});
    const title = 'Service areas';

    let subtitle1 = 'Your service areas are where you’ll receive leads with your free account.';
    if (isAdvertiser) subtitle1 = 'Your service areas are where you’ll receive leads.';

    const confirmationModalText = isAdvertiser ? 'This will remove all of your free service areas. Your paid areas will not change.'
      : 'This will remove all of your free service areas.';

    return (
      <div>
        <div className="service-area-map-wrapper container">
          <Grid>
            <Form
              onSubmit={handleSubmit(this.saveServiceArea)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            >
              <div className="service-area-map-sub-header">
                <Row className="service-area-margin-right-10" >
                  <Choose>
                    <When condition={showEditControls}>
                      <Row className={isAdvertiser ? 'service-area-header-row-adv' : 'service-area-header-row'}>
                        <Col xs={12} sm={isAdvertiser ? 12 : 7} >
                          <Row>
                            <Col xs={12} sm={12} className="service-area-storeless-title-wrapper">
                              <h2>{title}</h2>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={12} sm={12} className="service-area-subtitle">
                              {subtitle1}&nbsp;
                            </Col>
                          </Row>
                          <If condition={isAdvertiser}>
                            <Row style={{ marginTop: 24 }}>
                              <Col xs={12} sm={12}>
                                <i
                                  className="icon icon-square"
                                  style={{
                                  color: stylesColors.brandInfo,
                                  opacity: 0.5,
                                  marginBottom: 2,
                                }}
                                />
                                <span style={{ marginLeft: 3 }}> Show up in more customer
                                  searches here. To edit areas,  <a href={urlCatalog.global.businessCenterPhone()} id="call-us" style={{ color: '#02819E', fontWeight: 'bold' }}>call {urlCatalog.global.businessCenterPhoneText()} </a>
                                  or&nbsp;
                                  <a
                                    id="intercom-launcher"
                                    href={`mailto:${intercomAppId}@incoming.intercom.io`}
                                    style={{ color: '#02819E', fontWeight: 'bold' }}
                                  >chat online.&nbsp;
                                  </a>
                                </span>
                              </Col>
                              <Col xs={12} sm={12}>
                                <i
                                  className="icon icon-square"
                                  style={{
                                  color: stylesColors.brandPrimary,
                                  opacity: 0.5,
                                  marginBottom: 2,
                                }}
                                />
                                <span style={{ marginLeft: 3 }}> Get free advertising here.
                                  Edit areas directly on the map.
                                </span>
                              </Col>
                            </Row>
                          </If>
                        </Col>
                        <If condition={!this.state.editMode}>
                          <Col xs={12} sm={isAdvertiser ? 12 : 5}>
                            <Track>
                              <Button
                                id="edit-service-area-edit-button"
                                onClick={this.toggleEditMode}
                                bsStyle="primary-office"
                                className={editServiceAreaBtnClassNames(isNorthStar, isAdvertiser)}
                                flat
                              >
                                Edit service areas
                              </Button>
                            </Track>
                          </Col>
                        </If>
                        <If condition={this.state.editMode}>
                          <Col
                            xs={12}
                            sm={isAdvertiser ? 12 : 5}
                            className={!isAdvertiser ? 'flex-justify-right service-area-edit-button-flex' :
                            'service-area-edit-button-flex service-area-margin-32'}
                          >
                            <Button
                              id="edit-service-area-save-button"
                              bsSize="xsmall"
                              bsStyle="primary-office"
                              type="submit"
                              className={isNorthStar ? 'service-area-map-btn-ns' : ''}
                              flat
                              disabled={isLoading}
                              loading={isLoading}
                            >
                                Save
                            </Button>
                            <Button
                              id="edit-service-area-cancel-button"
                              bsSize="xsmall"
                              bsStyle="default-office"
                              className={isNorthStar ? 'service-area-map-btn-ns-sec' : ''}
                              onClick={this.cancelEditMode}
                              flat
                            >
                            Cancel
                            </Button>
                            <Track>
                              <Button
                                id="service-area-reset"
                                onClick={this.openResetServiceAreaModal}
                                bsStyle="link"
                                className={isNorthStar ? 'service-area-map-btn-ns-link text-info' : 'text-info'}
                                bsSize="small"
                              >
                                  Reset service areas
                              </Button>
                            </Track>
                          </Col>
                        </If>
                      </Row>
                      <Row>
                        <Col xs={12} sm={4} md={3} lg={3} className="edit-service-areas-wrapper">
                          <Modal
                            show={this.state.confirmEditServiceAreaModal}
                            onRequestHide={() => this.setState(() => ({
                            confirmEditServiceAreaModal: false,
                          }))}
                            id="edit-service-area-modal"
                          >
                            <Modal.Body>
                              <div className="modal-body">
                                <h4
                                  style={{ marginBottom: '1em', textAlign: 'center' }}
                                >
                                Do you wish to edit your service area?
                                </h4>
                                <Button
                                  id="edit-service-area-button-no"
                                  bsSize="small"
                                  bsStyle="default-office"
                                  style={{ marginRight: '1em' }}
                                  className="service-area-modal-button"
                                  onClick={() => this.setState(() => ({
                                  confirmEditServiceAreaModal: false,
                                }))}
                                  flat
                                >
                                No
                                </Button>
                                <Track>
                                  <Button
                                    id="edit-service-area-button-yes"
                                    bsSize="small"
                                    bsStyle="primary-office"
                                    className="service-area-modal-button"
                                    onClick={this.editServiceArea}
                                    flat
                                  >
                                  Yes
                                  </Button>
                                </Track>
                              </div>
                            </Modal.Body>
                          </Modal>
                          <Modal
                            backdrop="static"
                            show={this.state.confirmResetServiceAreaModal}
                            onRequestHide={() => this.setState(() => ({
                            confirmResetServiceAreaModal: false,
                          }))}
                            id="reset-service-area-modal"
                          >
                            <Modal.Body>
                              <div className="modal-body">
                                <h2>Are you sure?</h2>
                                <p>{confirmationModalText}</p>
                                <div className="reset-service-area-button-wrapper service-area-edit-button-flex flex-justify-center">
                                  <Track>
                                    <Button
                                      id="reset-service-area-button-yes"
                                      bsSize="small"
                                      bsStyle="primary-office"
                                      className={isNorthStar ? 'service-area-map-btn-ns' : 'service-area-modal-button'}
                                      onClick={this.resetServiceArea}
                                      flat
                                    >
                                    Reset service areas
                                    </Button>
                                  </Track>
                                  <Track>
                                    <Button
                                      id="reset-service-area-button-no"
                                      bsSize="small"
                                      className={isNorthStar ? 'service-area-map-btn-ns-sec' : 'service-area-modal-button'}
                                      onClick={() => this.setState(() => ({
                                      confirmResetServiceAreaModal: false,
                                    }))}
                                      flat
                                    >
                                    Cancel
                                    </Button>
                                  </Track>
                                </div>
                              </div>
                            </Modal.Body>
                          </Modal>
                        </Col>
                      </Row>
                    </When>
                    <Otherwise>
                      <Row>
                        <Col xs={12} sm={4} className="service-area-title-wrapper">
                          <h2>{title}</h2>
                        </Col>
                        <Col xs={12} sm={9} className="service-area-subtitle">
                          <p>{subtitle1}&nbsp;</p>
                        </Col>
                      </Row>
                    </Otherwise>
                  </Choose>
                </Row>
              </div>
            </Form>
          </Grid>
          <Row className="map-and-sidebar-wrapper map-container-flex">
            <Col
              className={classNames({
                'map-with-sidebar': this.state.citySelectionOpened,
                // eslint-disable-next-line quote-props
                'container': true,
              })}
            >
              <div
                className="service-area-map"
                ref={(el) => {
                  this.mapContainer = el;
                }}
              >
                <If condition={showEditAlert}>
                  <Alert
                    bsStyle="info"
                    onDismiss={() => this.setState({ showEditAlert: false })}
                    dismissAfter={dismissAlertAfter}
                  >
                    {renderAlertBodyWithIcon('Select a ZIP Code region on the map to edit your service area.', 'icon-info-circled')}
                  </Alert>
                </If>
                <If condition={showSavedAlert}>
                  <Alert
                    bsStyle="success"
                  >
                    {renderAlertBodyWithIcon('Your changes have been saved.', 'icon-check-bullet-o')}
                  </Alert>
                </If>
                <If condition={showClickedAdAreaAlert}>
                  <Alert
                    bsStyle="danger"
                    onDismiss={() => this.setState({ showClickedAdAreaAlert: false })}
                    dismissAfter={dismissAlertAfter}
                  >
                    {renderAlertBodyWithIcon('You cannot remove your paid areas from your service areas.', 'icon-attention')}
                  </Alert>
                </If>
                <If condition={error}>
                  <Alert
                    bsStyle="danger"
                    onDismiss={resetSetServiceArea}
                    dismissAfter={dismissAlertAfter}
                  >
                    There was an error processing your request.
                    Please reload the page or try again later.
                  </Alert>
                </If>
                <If condition={showZoomInAlert}>
                  <Alert
                    bsStyle="danger"
                    onDismiss={() => this.setState({ showZoomInAlert: false })}
                    dismissAfter={dismissAlertAfter}
                  >
                    Please zoom in further to modify your Service Area.
                  </Alert>
                </If>
                <If condition={isAdvertiser}>
                  <div className="desktop-legend hidden-xs">
                    <Row>
                      <Col sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                        <strong>Legend</strong>
                      </Col>
                      {generateLegend()}
                    </Row>
                  </div>
                  <div className="mobile-legend visible-xs">
                    <Row>
                      <Button
                        id="service-area-map--legend-toggle"
                        onClick={this.toggleLegend}
                        className="pull-right"
                        style={{
                          border: 0,
                          marginBottom: -1,
                          opacity: 0.8,
                          top: 0,
                        }}
                      >Legend <i className={`icon ${legendIcon}`} />
                      </Button>
                    </Row>

                    <Collapse
                      in={this.state.legendOpened}
                      style={{
                        backgroundColor: stylesColors.white,
                        padding: '1em 1em 3em 1em',
                        opacity: 0.8,
                        textAlign: 'center',
                      }}
                    >
                      <Row>
                        {generateLegend()}
                      </Row>
                    </Collapse>
                  </div>
                </If>
                <div id="map-navigational-controls" className="mapboxgl-ctrl-top-right">
                  <div className="mapboxgl-ctrl mapboxgl-ctrl-group">
                    <If condition={this.state.editMode}>
                      <ButtonWithTooltip
                        id="map-zip-list"
                        icon="list"
                        label="Toggle Zip Code List"
                        onClick={this.toggleCitySelection}
                      />
                    </If>
                    <ButtonWithTooltip
                      id="map-fly-to-default"
                      icon="location"
                      label="Center on Business"
                      onClick={this.returnToDefaultLocation}
                    />
                    <ButtonWithTooltip
                      id="map-zoom-in"
                      icon="plus"
                      label="Zoom In"
                      onClick={this.zoomIn}
                      additionalClasses="hidden-xs"
                    />
                    <ButtonWithTooltip
                      id="map-zoom-out"
                      icon="minus"
                      label="Zoom Out"
                      onClick={this.zoomOut}
                      additionalClasses="hidden-xs"
                    />
                  </div>
                </div>
              </div>
            </Col>
            <If condition={this.state.citySelectionOpened}>
              <Col xs={12} sm={6} md={4} lg={3} className="city-selection-wrapper" >
                <CitySelection
                  onClose={this.toggleCitySelection}
                  selectedPostalCodes={this.state.draftServiceArea}
                  onClickPostalCode={this.togglePostalCodeInDraftServiceArea}
                  onClickAdZone={this.sendAdZoneSelectEvent}
                />
              </Col>
            </If>
          </Row>
        </div>
      </div>
    );
  }
}

export default (compose(
  reduxForm({
    form: 'serviceAreaMapForm',
    validate: () => ({}),
    destroyOnUnmount: false,
  }),
  analytics({
    ruleset: serviceAreaMapAnalytics,
  }),
  connect(
    state => ({
      config: state.config,
      serviceProviderIdFromState: state.serviceProviderId,
      serviceProviderCache: state.serviceProviderCache,
      serviceAreaMapStore: state.serviceAreaMap,
    }),
    {
      ...serviceAreaMapActions,
      ...analyticsActions,
    },
  ),
  pure,
)(ServiceAreaMapComponent): any);



// WEBPACK FOOTER //
// ./src/app/browser/service-areas/ServiceAreaMap.js